/**
 * Pregnancy Calculator V2 JavaScript
 * Enhanced pregnancy due date calculator with comprehensive features
 */

(function () {
  "use strict";

  // Global variables for calculator state
  let currentMethod = "lmp";
  let calculatorInitialized = false;

  // DOM Elements
  let methodTabs;
  let methodForms;
  let calculateBtn;
  let errorMessage;
  let results;

  // Initialize calculator when DOM is loaded
  document.addEventListener("DOMContentLoaded", function () {
    initializeCalculator();
  });

  function initializeCalculator() {
    // Get DOM elements
    methodTabs = document.querySelectorAll(
      ".pregnancy-calculator-v2 .method-tab"
    );
    methodForms = document.querySelectorAll(
      ".pregnancy-calculator-v2 .method-form"
    );
    calculateBtn = document.querySelector(
      ".pregnancy-calculator-v2 #calculate-btn"
    );
    errorMessage = document.querySelector(
      ".pregnancy-calculator-v2 #error-message"
    );
    results = document.querySelector(".pregnancy-calculator-v2 #results");

    if (
      !methodTabs.length ||
      !methodForms.length ||
      !calculateBtn ||
      !errorMessage ||
      !results
    ) {
      console.error(
        "Required DOM elements not found for pregnancy calculator v2"
      );
      return;
    }

    // Set up tab switching
    methodTabs.forEach((tab) => {
      tab.addEventListener("click", function () {
        switchMethod(this.dataset.method);
      });
    });

    // Set up calculate button
    calculateBtn.addEventListener("click", function (e) {
      e.preventDefault();
      calculateDueDate();
    });

    // Set up form inputs for Enter key
    const allInputs = document.querySelectorAll(
      ".pregnancy-calculator-v2 input"
    );
    allInputs.forEach((input) => {
      input.addEventListener("keypress", function (e) {
        if (e.key === "Enter") {
          e.preventDefault();
          calculateDueDate();
        }
      });
    });

    // Set default max date to today for all date inputs
    const today = new Date().toISOString().split("T")[0];
    const lmpDateInput = document.querySelector(
      ".pregnancy-calculator-v2 #lmp-date"
    );
    const conceptionDateInput = document.querySelector(
      ".pregnancy-calculator-v2 #conception-date"
    );

    if (lmpDateInput) lmpDateInput.max = today;
    if (conceptionDateInput) conceptionDateInput.max = today;

    calculatorInitialized = true;
    console.log("Pregnancy calculator v2 initialized successfully");
  }

  function switchMethod(method) {
    currentMethod = method;

    // Update active tab
    methodTabs.forEach((tab) => {
      tab.classList.toggle("active", tab.dataset.method === method);
    });

    // Update active form
    methodForms.forEach((form) => {
      form.classList.toggle("active", form.id === `${method}-form`);
    });

    // Clear any previous errors
    hideError();
  }

  function calculateDueDate() {
    hideError();

    try {
      let dueDate;
      let conceptionDate;
      let lmpDate;

      if (currentMethod === "lmp") {
        const lmpInput = document.querySelector(
          ".pregnancy-calculator-v2 #lmp-date"
        ).value;
        const cycleLengthInput = document
          .querySelector(".pregnancy-calculator-v2 #cycle-length")
          .value.trim();
        let cycleLength = 28; // Always start with default

        // Only validate if user entered something other than empty or 0
        if (cycleLengthInput && cycleLengthInput !== "0") {
          const parsedCycle = parseInt(cycleLengthInput);
          if (!isNaN(parsedCycle) && parsedCycle >= 20 && parsedCycle <= 45) {
            cycleLength = parsedCycle;
          } else {
            showError(
              "Please enter a valid cycle length between 20 and 45 days."
            );
            return;
          }
        }
        // If empty or 0, cycleLength stays 28 (no error)

        if (!lmpInput) {
          showError("Please enter your last menstrual period date.");
          return;
        }

        lmpDate = new Date(lmpInput);

        // Validate LMP date is not in the future
        if (lmpDate > new Date()) {
          showError("Last menstrual period date cannot be in the future.");
          return;
        }

        // Calculate conception date (ovulation typically occurs 14 days before next period)
        const ovulationDay = cycleLength - 14;
        conceptionDate = new Date(lmpDate);
        conceptionDate.setDate(lmpDate.getDate() + ovulationDay);

        // Calculate due date (280 days from LMP)
        dueDate = new Date(lmpDate);
        dueDate.setDate(lmpDate.getDate() + 280);
      } else if (currentMethod === "conception") {
        const conceptionInput = document.querySelector(
          ".pregnancy-calculator-v2 #conception-date"
        ).value;

        if (!conceptionInput) {
          showError("Please enter your conception date.");
          return;
        }

        conceptionDate = new Date(conceptionInput);

        // Validate conception date is not in the future
        if (conceptionDate > new Date()) {
          showError("Conception date cannot be in the future.");
          return;
        }

        // Calculate LMP date (14 days before conception)
        lmpDate = new Date(conceptionDate);
        lmpDate.setDate(conceptionDate.getDate() - 14);

        // Calculate due date (266 days from conception)
        dueDate = new Date(conceptionDate);
        dueDate.setDate(conceptionDate.getDate() + 266);
      }

      // Validate calculated dates
      if (!dueDate || !conceptionDate || !lmpDate) {
        showError(
          "Error calculating dates. Please check your input and try again."
        );
        return;
      }

      // Display results
      displayResults(dueDate, lmpDate, conceptionDate);
    } catch (error) {
      console.error("Calculation error:", error);
      showError(
        "An error occurred while calculating your due date. Please try again."
      );
    }
  }

  function displayResults(dueDate, lmpDate, conceptionDate) {
    // Calculate current pregnancy progress
    const today = new Date();
    const pregnancyStart = lmpDate;
    const totalDuration = dueDate.getTime() - pregnancyStart.getTime();
    const elapsed = today.getTime() - pregnancyStart.getTime();

    const weeksPregnant = Math.floor(elapsed / (7 * 24 * 60 * 60 * 1000));
    const daysIntoWeek = Math.floor(
      (elapsed % (7 * 24 * 60 * 60 * 1000)) / (24 * 60 * 60 * 1000)
    );

    // Display due date
    const dueDateDisplay = document.querySelector(
      ".pregnancy-calculator-v2 #due-date-display"
    );
    if (dueDateDisplay) {
      dueDateDisplay.textContent = formatDate(dueDate);
    }

    // Display pregnancy progress
    const progressElement = document.querySelector(
      ".pregnancy-calculator-v2 #pregnancy-progress"
    );
    if (progressElement) {
      const progressText =
        weeksPregnant >= 0 && weeksPregnant <= 42
          ? `You are ${weeksPregnant} weeks and ${daysIntoWeek} days pregnant`
          : `Based on your dates, your baby was due ${formatDate(dueDate)}`;

      progressElement.textContent = progressText;
    }

    // Update trimester timeline
    updateTrimesterTimeline(weeksPregnant);

    // Generate timeline milestones
    generateTimeline(lmpDate, dueDate);

    // Generate fun facts
    generateFunFacts(weeksPregnant);

    // Show results with animation
    results.classList.add("show");
    results.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function updateTrimesterTimeline(weeksPregnant) {
    const trimesters = [
      {
        element: document.querySelector(
          ".pregnancy-calculator-v2 #trimester-1"
        ),
        start: 1,
        end: 12,
      },
      {
        element: document.querySelector(
          ".pregnancy-calculator-v2 #trimester-2"
        ),
        start: 13,
        end: 26,
      },
      {
        element: document.querySelector(
          ".pregnancy-calculator-v2 #trimester-3"
        ),
        start: 27,
        end: 40,
      },
    ];

    trimesters.forEach((trimester) => {
      const { element, start, end } = trimester;
      if (!element) return;

      const fill = element.querySelector(".trimester-fill");
      if (!fill) return;

      element.classList.remove("active", "completed");

      if (weeksPregnant < start) {
        // Not reached yet
        fill.style.width = "0%";
      } else if (weeksPregnant >= start && weeksPregnant <= end) {
        // Currently in this trimester
        element.classList.add("active");
        const progress =
          ((weeksPregnant - start + 1) / (end - start + 1)) * 100;
        fill.style.width = Math.min(progress, 100) + "%";
      } else {
        // Completed
        element.classList.add("completed");
        fill.style.width = "100%";
      }
    });
  }

  function generateTimeline(lmpDate, dueDate) {
    const timeline = document.querySelector(
      ".pregnancy-calculator-v2 #timeline"
    );
    if (!timeline) return;

    const milestones = [
      { weeks: 4, event: "First missed period", icon: "📅" },
      { weeks: 6, event: "Baby's heart begins to beat", icon: "💓" },
      { weeks: 8, event: "First prenatal appointment", icon: "👩‍⚕️" },
      { weeks: 12, event: "End of first trimester", icon: "🎉" },
      { weeks: 16, event: "You might feel first movements", icon: "🤸‍♀️" },
      { weeks: 20, event: "Anatomy scan - find out gender!", icon: "👶" },
      { weeks: 24, event: "Viability milestone reached", icon: "🌟" },
      { weeks: 28, event: "Third trimester begins", icon: "🎯" },
      { weeks: 32, event: "Baby's bones are hardening", icon: "🦴" },
      { weeks: 36, event: "Baby is considered full-term soon", icon: "✨" },
      { weeks: 40, event: "Your due date!", icon: "🎊" },
    ];

    timeline.innerHTML = milestones
      .map((milestone) => {
        const milestoneDate = new Date(lmpDate);
        milestoneDate.setDate(lmpDate.getDate() + milestone.weeks * 7);

        return `
        <div class="timeline-item">
          <div class="timeline-date">${formatDate(milestoneDate)}</div>
          <div class="timeline-event">${milestone.icon} ${milestone.event}</div>
          <div class="timeline-weeks">Week ${milestone.weeks}</div>
        </div>
      `;
      })
      .join("");
  }

  function generateFunFacts(weeksPregnant) {
    const funFactsContainer = document.querySelector(
      ".pregnancy-calculator-v2 #fun-facts"
    );
    if (!funFactsContainer) return;

    const allFacts = [
      {
        icon: "👶",
        title: "Baby Size",
        description: getBabySize(weeksPregnant),
      },
      {
        icon: "💓",
        title: "Heart Rate",
        description: getHeartRate(weeksPregnant),
      },
      {
        icon: "📏",
        title: "Development",
        description: getDevelopment(weeksPregnant),
      },
      {
        icon: "🤱",
        title: "Your Body",
        description: getBodyChanges(weeksPregnant),
      },
      {
        icon: "♈",
        title: "Horoscope",
        description: getHoroscope(
          new Date(Date.now() + (280 - weeksPregnant * 7) * 24 * 60 * 60 * 1000)
        ),
      },
      {
        icon: "💎",
        title: "Birthstone",
        description: getBirthstone(
          new Date(Date.now() + (280 - weeksPregnant * 7) * 24 * 60 * 60 * 1000)
        ),
      },
      {
        icon: "🍎",
        title: "Nutrition Tip",
        description: getNutritionTip(weeksPregnant),
      },
      {
        icon: "🎯",
        title: "This Week",
        description: getWeeklyTip(weeksPregnant),
      },
    ];

    funFactsContainer.innerHTML = allFacts
      .map(
        (fact) => `
      <div class="fun-fact">
        <div class="fun-fact-icon">${fact.icon}</div>
        <div class="fun-fact-title">${fact.title}</div>
        <div class="fun-fact-description">${fact.description}</div>
      </div>
    `
      )
      .join("");
  }

  function getBabySize(weeks) {
    const sizes = {
      4: "Poppy seed (2mm)",
      5: "Apple seed (3mm)",
      6: "Pea (5mm)",
      7: "Blueberry (1cm)",
      8: "Raspberry (1.5cm)",
      9: "Cherry (2cm)",
      10: "Strawberry (3cm)",
      11: "Lime (4cm)",
      12: "Plum (5cm)",
      16: "Apple (12cm)",
      20: "Banana (25cm)",
      24: "Corn (30cm)",
      28: "Eggplant (35cm)",
      32: "Squash (40cm)",
      36: "Lettuce (45cm)",
      40: "Watermelon (50cm)",
    };

    for (let w = weeks; w >= 4; w--) {
      if (sizes[w]) return sizes[w];
    }
    return "Still very tiny!";
  }

  function getHeartRate(weeks) {
    if (weeks < 6) return "Heart is just beginning to form";
    if (weeks < 9) return "160-180 beats per minute";
    if (weeks < 20) return "140-160 beats per minute";
    return "120-140 beats per minute";
  }

  function getDevelopment(weeks) {
    const developments = {
      4: "Neural tube is forming",
      6: "Limb buds are developing",
      8: "All major organs present",
      10: "Fingers and toes forming",
      12: "Can make facial expressions",
      16: "Can hear your voice",
      20: "Fingerprints are forming",
      24: "Brain waves can be detected",
      28: "Eyes can open and close",
      32: "Bones are hardening",
      36: "Lungs are maturing",
      40: "Ready for the outside world!",
    };

    for (let w = weeks; w >= 4; w--) {
      if (developments[w]) return developments[w];
    }
    return "Amazing development happening!";
  }

  function getBodyChanges(weeks) {
    const changes = {
      4: "You might notice breast tenderness",
      6: "Morning sickness may begin",
      8: "Fatigue is common",
      10: "Your waist may start to thicken",
      12: "Morning sickness often improves",
      16: "You might feel first movements",
      20: "Your belly is clearly showing",
      24: "You may experience back pain",
      28: "Braxton Hicks contractions may start",
      32: "You might feel short of breath",
      36: "Baby is taking up lots of space",
      40: "Your body is ready for labor",
    };

    for (let w = weeks; w >= 4; w--) {
      if (changes[w]) return changes[w];
    }
    return "Your body is doing amazing work!";
  }

  function getNutritionTip(weeks) {
    const tips = [
      "Take 400mcg folic acid daily",
      "Eat iron-rich foods like spinach",
      "Include calcium for baby's bones",
      "Omega-3s support brain development",
      "Stay hydrated with plenty of water",
      "Protein helps baby grow strong",
    ];
    return tips[Math.floor(weeks / 7) % tips.length];
  }

  function getWeeklyTip(weeks) {
    const tips = [
      "Start taking prenatal vitamins",
      "Schedule your first prenatal appointment",
      "Begin documenting your pregnancy journey",
      "Consider pregnancy yoga or gentle exercise",
      "Start thinking about baby names",
      "Plan your nursery space",
    ];
    return tips[Math.floor(weeks / 7) % tips.length];
  }

  function getHoroscope(dueDate) {
    const month = dueDate.getMonth() + 1;
    const day = dueDate.getDate();

    const signs = [
      {
        name: "Capricorn",
        start: [12, 22],
        end: [1, 19],
        traits: "Ambitious, practical, and disciplined",
      },
      {
        name: "Aquarius",
        start: [1, 20],
        end: [2, 18],
        traits: "Independent, innovative, and humanitarian",
      },
      {
        name: "Pisces",
        start: [2, 19],
        end: [3, 20],
        traits: "Compassionate, artistic, and intuitive",
      },
      {
        name: "Aries",
        start: [3, 21],
        end: [4, 19],
        traits: "Energetic, confident, and pioneering",
      },
      {
        name: "Taurus",
        start: [4, 20],
        end: [5, 20],
        traits: "Reliable, patient, and determined",
      },
      {
        name: "Gemini",
        start: [5, 21],
        end: [6, 20],
        traits: "Curious, adaptable, and communicative",
      },
      {
        name: "Cancer",
        start: [6, 21],
        end: [7, 22],
        traits: "Nurturing, intuitive, and protective",
      },
      {
        name: "Leo",
        start: [7, 23],
        end: [8, 22],
        traits: "Confident, generous, and creative",
      },
      {
        name: "Virgo",
        start: [8, 23],
        end: [9, 22],
        traits: "Analytical, helpful, and detail-oriented",
      },
      {
        name: "Libra",
        start: [9, 23],
        end: [10, 22],
        traits: "Diplomatic, charming, and fair-minded",
      },
      {
        name: "Scorpio",
        start: [10, 23],
        end: [11, 21],
        traits: "Passionate, resourceful, and brave",
      },
      {
        name: "Sagittarius",
        start: [11, 22],
        end: [12, 21],
        traits: "Adventurous, optimistic, and philosophical",
      },
    ];

    for (const sign of signs) {
      const [startMonth, startDay] = sign.start;
      const [endMonth, endDay] = sign.end;

      if (
        (month === startMonth && day >= startDay) ||
        (month === endMonth && day <= endDay)
      ) {
        return `${sign.name} - ${sign.traits}`;
      }
    }
    return "Your baby will be unique and wonderful!";
  }

  function getBirthstone(dueDate) {
    const month = dueDate.getMonth();
    const birthstones = [
      "Garnet - Symbolises protection and strength",
      "Amethyst - Represents peace and clarity",
      "Aquamarine - Brings calm and courage",
      "Diamond - Signifies purity and invincibility",
      "Emerald - Represents growth and renewal",
      "Pearl - Symbolises wisdom and integrity",
      "Ruby - Brings passion and vitality",
      "Peridot - Represents positive energy",
      "Sapphire - Signifies loyalty and truth",
      "Opal - Brings creativity and inspiration",
      "Topaz - Represents strength and healing",
      "Turquoise - Symbolises protection and good fortune",
    ];
    return birthstones[month];
  }

  function formatDate(date) {
    const weekdays = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const weekday = weekdays[date.getDay()];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${weekday} ${day} ${month} ${year}`;
  }

  function showError(message) {
    if (errorMessage) {
      errorMessage.textContent = message;
      errorMessage.classList.add("show");
    }
  }

  function hideError() {
    if (errorMessage) {
      errorMessage.classList.remove("show");
    }
  }

  // Expose functions for external use if needed
  window.PregnancyCalculatorV2 = {
    initialize: initializeCalculator,
    calculate: calculateDueDate,
    switchMethod: switchMethod,
  };
})();
